import { z } from 'zod';
import { PatientIdSchema, ProcedureIdSchema, UserIdSchema } from './common';

/**
 * Estados de un episodio clínico. La máquina de estados es estrictamente lineal
 * excepto por la transición a "anulado" que puede ocurrir desde cualquier estado abierto.
 *
 *   abierto → consentimiento → anestesia → gasto → cerrado
 *      ↘──────────────────────────────────────────→ anulado
 */
export const EpisodeStatusSchema = z.enum([
  'abierto',
  'consentimiento',
  'anestesia',
  'gasto',
  'cerrado',
  'anulado',
]);
export type EpisodeStatus = z.infer<typeof EpisodeStatusSchema>;

/** Transiciones permitidas. La fuente de verdad vive aquí — el guardia del dominio la consume. */
export const EpisodeTransitions: Record<EpisodeStatus, readonly EpisodeStatus[]> = {
  abierto: ['consentimiento', 'anulado'],
  consentimiento: ['anestesia', 'anulado'],
  anestesia: ['gasto', 'anulado'],
  gasto: ['cerrado', 'anulado'],
  cerrado: [],
  anulado: [],
} as const;

/**
 * Labels en español para mostrar el estado del episodio. Portado desde
 * `assets/js/data.js` (objeto `estados`).
 */
export const EPISODE_STATUS_LABELS: Record<EpisodeStatus, string> = {
  abierto: 'Abierto',
  consentimiento: 'Consentimiento firmado',
  anestesia: 'Anestesia diligenciada',
  gasto: 'Hoja de gasto registrada',
  cerrado: 'Cerrado',
  anulado: 'Anulado',
} as const;

/**
 * Variante visual del badge para cada estado. El frontend mapea estas claves
 * a sus clases Tailwind via `<Badge variant=...>`. Mantener la unión cerrada
 * para que el compilador detecte estados sin variante asignada.
 */
export type EpisodeStatusVariant =
  | 'gray'
  | 'blue'
  | 'purple'
  | 'amber'
  | 'teal'
  | 'green'
  | 'red';

/**
 * Estado operativo del paciente dentro del flujo del día.
 * Independiente del `EpisodeStatus` (que rastrea documentos firmados):
 * esto rastrea dónde está físicamente el paciente y lo actualiza la
 * enfermera de manera manual.
 *   - `sala_espera`: el paciente llegó y está en sala (estado inicial).
 *   - `canalizacion`: ya está en sala, canalizado y listo para entrar.
 *   - `finalizado`: el procedimiento terminó, el paciente salió de sala.
 */
export const PatientFlowStatusSchema = z.enum([
  'sala_espera',
  'canalizacion',
  'finalizado',
]);
export type PatientFlowStatus = z.infer<typeof PatientFlowStatusSchema>;

export const PATIENT_FLOW_LABELS: Record<PatientFlowStatus, string> = {
  sala_espera: 'En sala de espera',
  canalizacion: 'Canalización',
  finalizado: 'Finalizado',
};

export const PATIENT_FLOW_VARIANTS: Record<PatientFlowStatus, EpisodeStatusVariant> = {
  sala_espera: 'amber',
  canalizacion: 'blue',
  finalizado: 'green',
};

export const PATIENT_FLOW_ORDER: readonly PatientFlowStatus[] = [
  'sala_espera',
  'canalizacion',
  'finalizado',
];

export const EPISODE_STATUS_VARIANTS: Record<EpisodeStatus, EpisodeStatusVariant> = {
  abierto: 'gray',
  consentimiento: 'blue',
  anestesia: 'purple',
  gasto: 'amber',
  cerrado: 'green',
  anulado: 'red',
} as const;

/**
 * Tipo de procedimiento del catálogo Endomedical. El catálogo completo (con CUPS
 * y consentimientos asociados) vive en `./procedures-catalog`. Aquí se relaja el
 * schema a string para soportar las ~55 entradas del catálogo y permitir agregar
 * variantes sin migrar tipos. La validación funcional (que el `tipo` exista en
 * el catálogo) la hace la UI mediante el dropdown poblado por
 * `listProcedureNames()`.
 */
export const ProcedureTypeSchema = z.string().min(2).max(300);
export type ProcedureType = z.infer<typeof ProcedureTypeSchema>;

export const RoomSchema = z.enum(['Sala 1', 'Sala 2', 'Sala 3']);
export type Room = z.infer<typeof RoomSchema>;

/**
 * Catálogo oficial de entidades responsables (EPS, prepagadas, particulares,
 * convenios e IPS aliadas) que aparece como dropdown en el campo "Entidad"
 * del formulario de procedimiento. Mantener este array sincronizado con la
 * lista que provee el área administrativa de Endomedical.
 */
export const ENTIDADES_CATALOG: readonly string[] = [
  'ADUEN SALUD S.A.S',
  'ALLIANZ SEGUROS DE VIDA S.A',
  'CATHERINE DEL ROSARIO FIORILLO MORENO',
  'CENTRO CANCEROLOGICO DEL CARIBE LIMITADA CECAC',
  'CENTRO COLOPROCTOLOGIA Y ENDOSCOPIA DIGESTIVA DR',
  'COLMÉDICA MEDICINA PREPAGADA S.A',
  'COLSANITAS S.A.',
  'COOMEVA MEDICINA PREPAGADA',
  'COOPERATIVA DE TRABAJO ASOCIADO INTEGRAL EN SALUD',
  'COOSALUD EPS S.A.',
  'ELEMENTHAL HEALTH IPS S.A.S.',
  'ELIKA LORETH LUQUE ARIZA',
  'EPS SURAMERICANA S.A',
  'INVERSIONES CURE CORREA SAS',
  'JESUS MARIA PEREZ OROZCO',
  'JOAQUIN EDUARDO RODRIGUEZ GOMEZ',
  'MARIA NATALIA MORENO PISCIOTTI',
  'MAURICIO JOSE LOGREIRA LA VALLE',
  'MEDISANITAS SAS',
  'MEDPLUS MEDICINA PREPAGADA S.A.',
  'NETCARE BUSINESS S.A.S',
  'ORGANIZACION CLINICA BONNADONA PREVENIR S.A.S',
  'PROBIENESTAR S.A.S',
  'SALUD TOTAL EPS-S S.A.',
  'SEGUROS DE VIDA SURAMERICANA S.A',
  'SOCIEDAD MEDICO QUIRURGICA FARSIR S.A.S',
  'SUMINISTROS & SERVIMEDIC S.A.S',
  'TU SALUD PLUS IPS S.A.S',
  'UNION TEMPORAL NORSALUD PPL',
  'PARTICULAR',
  'ORGANIZACIÓN CLINICA GENERAL DEL NORTE S.A.S.',
  'JA SURGERY GROUP S.A.S.',
  'CORTESIA',
] as const;

export const DocumentStatusSchema = z.enum(['pendiente', 'borrador', 'firmado']);
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

export const EpisodeDocsSchema = z.object({
  consentimiento: DocumentStatusSchema,
  anestesia: DocumentStatusSchema,
  gasto: DocumentStatusSchema,
});
export type EpisodeDocs = z.infer<typeof EpisodeDocsSchema>;

export const ProcedureSchema = z.object({
  id: ProcedureIdSchema,
  pacienteId: PatientIdSchema,
  fecha: z.string().date(),
  hora: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:mm requerido'),
  sala: RoomSchema.optional(),
  tipo: ProcedureTypeSchema,
  medicoId: UserIdSchema,
  anestesiologoId: UserIdSchema.optional(),
  enfermeraId: UserIdSchema.optional(),
  entidad: z.string().max(120).optional(),
  /**
   * Procedimientos adicionales realizados en el mismo episodio. Un paciente
   * puede recibir 2+ procedimientos en la misma sesión (ej. colonoscopia +
   * endoscopia el mismo día). El `tipo` principal sigue siendo uno y este
   * arreglo lleva los adicionales — la hoja de gasto y el registro de
   * anestesia se llenan UNA sola vez para todo el episodio, pero el
   * consentimiento incluye PDFs de cada procedimiento. Para conteo en
   * payroll de enfermería, el episodio cuenta como (1 + tiposAdicionales).
   */
  tiposAdicionales: z.array(ProcedureTypeSchema).max(8).optional(),
  estado: EpisodeStatusSchema,
  /**
   * Estado operativo del paciente (flujo del día). Actualizado manualmente
   * por la enfermera. Independiente del `estado` documental.
   * Default: `sala_espera` al crear el episodio.
   */
  estadoPaciente: PatientFlowStatusSchema.default('sala_espera'),
  observaciones: z.string().max(2000).optional(),
  docs: EpisodeDocsSchema,
  createdAt: z.string().datetime().optional(),
  closedAt: z.string().datetime().optional(),
  /** Motivo solo presente cuando `estado='anulado'`. */
  motivoCancelacion: z.string().max(500).optional(),
  /** Timestamp de cancelación, presente solo cuando `estado='anulado'`. */
  canceladoAt: z.string().datetime().optional(),
});
export type Procedure = z.infer<typeof ProcedureSchema>;

export const CreateProcedureSchema = ProcedureSchema.pick({
  pacienteId: true,
  fecha: true,
  hora: true,
  sala: true,
  tipo: true,
  tiposAdicionales: true,
  medicoId: true,
  anestesiologoId: true,
  enfermeraId: true,
  entidad: true,
  observaciones: true,
});
export type CreateProcedureInput = z.infer<typeof CreateProcedureSchema>;

/**
 * Update permitido por `PATCH /api/procedures/:id`. NO incluye `estado`
 * (controlado por la máquina vía /transition), ni `pacienteId`/`tipo`/`medicoId`
 * (inmutables tras crear). Los opcionales aceptan `null` para limpiarlos.
 */
export const UpdateProcedureSchema = z
  .object({
    fecha: z.string().date(),
    hora: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:mm requerido'),
    sala: RoomSchema.nullable(),
    anestesiologoId: UserIdSchema.nullable(),
    enfermeraId: UserIdSchema.nullable(),
    entidad: z.string().max(120).nullable(),
    observaciones: z.string().max(2000).nullable(),
  })
  .partial();
export type UpdateProcedureInput = z.infer<typeof UpdateProcedureSchema>;

/** Body de `POST /api/procedures/:id/transition`. */
export const TransitionProcedureSchema = z.object({
  to: EpisodeStatusSchema,
  observaciones: z.string().max(2000).optional(),
});
export type TransitionProcedureInput = z.infer<typeof TransitionProcedureSchema>;

/** Body de `POST /api/procedures/:id/cancel`. */
export const CancelProcedureSchema = z.object({
  motivo: z.string().min(3).max(500),
});
export type CancelProcedureInput = z.infer<typeof CancelProcedureSchema>;

/**
 * Respuesta de `POST /api/procedures`. Las credenciales del portal del paciente
 * son siempre su documento (cédula), así que no se devuelve nada extra aquí.
 */
export const CreateProcedureResponseSchema = ProcedureSchema;
export type CreateProcedureResponse = z.infer<typeof CreateProcedureResponseSchema>;

/**
 * Body de `POST /api/procedures/:id/patient-status`.
 * Permitido a enfermera, médico tratante y admin. No tiene máquina de
 * estados estricta: la enfermera puede pasar a cualquiera de los 3 según
 * la realidad del flujo.
 */
export const UpdatePatientFlowSchema = z.object({
  estadoPaciente: PatientFlowStatusSchema,
});
export type UpdatePatientFlowInput = z.infer<typeof UpdatePatientFlowSchema>;

/**
 * Item devuelto por `GET /api/procedures` — extiende `Procedure` con
 * los nombres del paciente y del médico ya resueltos (join ligero).
 * Evita un round-trip por item desde el frontend.
 */
export const ProcedureListItemSchema = ProcedureSchema.extend({
  pacienteNombre: z.string(),
  medicoNombre: z.string(),
});
export type ProcedureListItem = z.infer<typeof ProcedureListItemSchema>;

/** Filtros aceptados por `GET /api/procedures`. */
export const ProceduresListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  fecha: z.string().date().optional(),
  desde: z.string().date().optional(),
  hasta: z.string().date().optional(),
  sala: RoomSchema.optional(),
  tipo: ProcedureTypeSchema.optional(),
  estado: EpisodeStatusSchema.optional(),
  estadoPaciente: PatientFlowStatusSchema.optional(),
  medicoId: UserIdSchema.optional(),
  pacienteId: PatientIdSchema.optional(),
});
export type ProceduresListQuery = z.infer<typeof ProceduresListQuerySchema>;
